'use client'

import { useCallback, useMemo, useState } from 'react'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import { cx } from 'class-variance-authority'
import { useLocale, useTranslations } from 'next-intl'
import { SignatureIcon } from '@phosphor-icons/react/dist/ssr/Signature'
import type { ColumnDef } from '@tanstack/react-table'
import { FileArrowDownIcon } from '@phosphor-icons/react/dist/ssr/FileArrowDown'
import { Badge, Button, Table, Toast, Tooltip } from '@skop-app/lib-front'
import type { Column } from '@skop-app/lib-front'
import { DataTable } from '@skop-app/lib-front/data-table'
import {
  orderStatusLabel,
  requestTypes,
  signStatusLabel,
} from '@skop-app/lib-domain/constants'
import type {
  LabelWithRole,
  Order,
  OrderView,
  SupportedLanguages,
} from '@skop-app/lib-domain/types'
import { formatPrice } from '@skop-app/lib-domain/utils'
import { sumOrdersByCurrency } from '@/features/quote/lib/orderCurrency'
import { DynamicIcon } from '@/components/atoms/DynamicIcon'

import { OrderModalValues } from '@/types'
import { useQuoteContext } from '@/context'
import { useRouter } from '@/navigation'
import { useUpdateOrder } from '@/services/mutations'
import { downloadSignDocument } from '@/services/queries/sign'

import { PDFDownloader, PDFInvoice } from '../PDF'
import { useOrderAuthorization } from '@/hooks'

type OrderListProps = {
  orders: Order[] | undefined
  view: OrderView
  isFetched: boolean
  disabled?: boolean
  className?: string
  teammates: LabelWithRole[]
}

type OrderActionsCellProps = {
  order: Order
  view: OrderView
}

function OrderActionsCell({ order, view }: Readonly<OrderActionsCellProps>) {
  const t = useTranslations()
  const { mutate: updateOrder } = useUpdateOrder()
  const { setPage, setCustomTitle, setOptions, setOrderID, setOrderType } =
    useQuoteContext()
  const { isAllowed } = useOrderAuthorization(order)

  return (
    <Table.RowActions
      options={[
        ...(view === 'seller'
          ? [
              {
                label: t('Documents-directory.transfer_voucher'),
                disabled:
                  order.status === 'CANCELED' || order.items?.length < 1,
                onClick: () => {
                  setPage('orders')
                  setOrderID(order.id)
                  setOrderType('CART')
                  setOptions({
                    hideClient: true,
                    hideAddProducts: true,
                    isTransferVoucher: true,
                  })
                  setCustomTitle(t('Order.transferOrderSignRequest'))
                },
              },
              {
                label: t('Order.deleteOrder'),
                disabled: order.status !== 'CANCELED',
                onClick: () => {
                  updateOrder({
                    input: {
                      id: order.id,
                      status: 'DELETED',
                    },
                  })
                  Toast.success(t('Order.deleteOrderSuccess'), {
                    duration: 1000,
                  })
                },
                className: 'hover:bg-red-500 hover:text-white',
              },
            ]
          : []),
        ...(view === 'sentquote'
          ? [
              {
                label: t('Documents-directory.signature'),
                disabled: order.status === 'DONE' || order.items?.length < 1,
                onClick: () => {
                  setPage('orders')
                  setOrderID(order.id)
                  setOrderType('FAVORITE')
                  setCustomTitle(t('Order.quoteSign'))
                },
              },
              {
                label: t('Documents-directory.delete_quote'),
                disabled: !isAllowed,
                onClick: () => {
                  updateOrder({
                    input: {
                      id: order.id,
                      status: 'DELETED',
                    },
                  })
                  Toast.success(t('Documents-directory.delete_quote_success'), {
                    duration: 1000,
                  })
                },
                className: 'hover:bg-red-500 hover:text-white',
              },
            ]
          : []),
      ]}
    />
  )
}

export const OrderList = ({
  orders,
  view,
  isFetched,
  teammates,
}: OrderListProps) => {
  const locale = useLocale()
  const router = useRouter()
  const t = useTranslations()
  const pathname = usePathname()
  const { setPage, setOrderType, setSuccessConfirmationModal } =
    useQuoteContext()

  const [filteredOrders, setFilteredOrders] = useState<Order[]>([])
  const [hasReceivedFilteredRows, setHasReceivedFilteredRows] = useState(false)

  const handleFilteredRowsChange = useCallback((rows: Order[]) => {
    setHasReceivedFilteredRows(true)
    setFilteredOrders(rows)
  }, [])

  const [downloadingDocuments, setDownloadingDocuments] = useState<Set<string>>(
    new Set(),
  )

  const handleDownloadSignedDocument = async (
    yousignID: string | undefined,
  ) => {
    if (!yousignID) {
      Toast.error(t('Documents-directory.download_error'))
      return
    }

    try {
      setDownloadingDocuments((prev) => new Set(prev).add(yousignID))

      await downloadSignDocument(yousignID)
      Toast.success(t('Documents-directory.download_success'))
    } catch (error) {
      console.error('Error downloading document:', error)
      Toast.error(t('Documents-directory.download_error'))
    }
    setDownloadingDocuments((prev) => {
      const newSet = new Set(prev)
      newSet.delete(yousignID)
      return newSet
    })
  }

  const colName = (view: OrderView) => {
    switch (view) {
      case 'buyer':
        return t('Transactions.order')
      case 'seller':
      case 'sentquote':
        return t('Team.CLIENT')
      case 'request':
        return t('Transactions.applicant')
      default:
        return t('Account.last_name')
    }
  }

  const getName = (row: { original: Order }) => {
    if (view === 'seller' || view === 'request' || view === 'sentquote') {
      return row.original?.name || row.original?.company || '--'
    }

    return row.original?.toUser?.name || row.original?.shop?.name || '--'
  }

  const ordersColumns: ColumnDef<Order>[] = [
    {
      accessorKey: 'name',
      meta: {
        label: colName(view),
        csvLabel: colName(view),
      },
      accessorFn: (row) => getName({ original: row }),
      filterFn: Table.filterFn,
      header: ({ column }) => (
        <Table.HeaderSort column={column as Column}>
          {colName(view)}
        </Table.HeaderSort>
      ),
      cell: ({ row }) => (
        <Link
          href={`${pathname}/details/${row.original.id}`}
          className="flex items-center"
        >
          {row.renderValue('name')}
        </Link>
      ),
    },
    {
      accessorKey: 'requestType',
      meta: {
        label: t('Property.type'),
        csvLabel: t('Property.type'),
      },
      accessorFn: (row) =>
        requestTypes.find((type) => type.value === row.requestType)?.label,
      filterFn: Table.filterFn,
      header: ({ column }) => {
        return (
          <Table.HeaderFilter
            label={t('Property.type')}
            column={column as Column}
            options={requestTypes.filter((type) =>
              type.applyTo?.includes(view),
            )}
          />
        )
      },
      cell: ({ row }) => (
        <Link
          href={`${pathname}/details/${row.original.id}`}
          className={cx('flex items-center')}
        >
          {row.renderValue('requestType')}
        </Link>
      ),
    },
    ...(view !== 'request'
      ? [
          {
            accessorKey: 'subTotal',
            meta: {
              label: t('Transactions.amount') + ' (HT)',
              csvLabel: t('Transactions.amount') + ' (HT)',
            },
            header: ({ column }) => (
              <Table.HeaderSort column={column}>
                {t('Transactions.amount')} (HT)
              </Table.HeaderSort>
            ),
            cell: ({ row }) => (
              <p className="text-center">{row.original?.subTotal ?? '-'}</p>
            ),
            maxSize: 120,
          },
        ]
      : []),
    ...(view !== 'request'
      ? [
          {
            accessorKey: 'total',
            meta: {
              label: t('Transactions.amount') + ' (TTC)',
              csvLabel: t('Transactions.amount') + ' (TTC)',
            },
            header: ({ column }) => (
              <Table.HeaderSort column={column}>
                {t('Transactions.amount')} (TTC)
              </Table.HeaderSort>
            ),
            cell: ({ row }) => (
              <p className="text-center">{row.original?.total ?? '-'}</p>
            ),
            maxSize: 120,
          },
        ]
      : []),
    {
      accessorKey: 'status',
      meta: {
        label: t('Field.action'),
        csvLabel: t('Field.action'),
      },
      accessorFn: (row) =>
        orderStatusLabel.find((status) => status.value === row.status)?.label,
      filterFn: Table.filterFn,
      header: ({ column }) => {
        return (
          <Table.HeaderFilter
            label={t('Field.action')}
            column={column as Column}
            options={orderStatusLabel.filter((status) =>
              status.applyTo.includes(view),
            )}
          />
        )
      },
      cell: ({ row }) => (
        <Link
          href={`${pathname}/details/${row.original.id}`}
          className="flex w-full items-center justify-center"
        >
          <Badge
            variant={row.original.statusFormatted?.styleVariant}
            className="font-medium"
          >
            <DynamicIcon
              name={row.original.statusFormatted?.icon ?? 'Question'}
              className="mr-1"
            />
            <p className="line-clamp-1">{row.renderValue('status')}</p>
          </Badge>
        </Link>
      ),
    },
    ...(view === 'seller' || view === 'sentquote'
      ? [
          {
            accessorKey: 'managerID',
            meta: {
              label: t('Order.manager'),
              csvLabel: t('Order.manager'),
            },
            filterFn: Table.filterFn,
            accessorFn: (row) =>
              teammates?.find((teammate) => teammate.value === row.managerID)
                ?.label,
            header: ({ column }) => (
              <Table.HeaderFilter
                label={t('Order.manager')}
                column={column as Column}
                options={
                  teammates?.filter((teammate) => teammate.role !== 'GUEST') ||
                  []
                }
              />
            ),
            cell: ({ row }) => {
              const manager = teammates?.find(
                (teammate) => teammate.value === row.original?.managerID,
              )

              return <p className="truncate">{manager?.label ?? '-'}</p>
            },
          },
        ]
      : []),
    ...(view === 'request' || view === 'seller' || view === 'sentquote'
      ? [
          {
            accessorKey: 'yousignStatus',
            meta: {
              label: t('Documents-directory.signature'),
              csvLabel: t('Documents-directory.signature'),
            },
            filterFn: Table.filterFn,
            accessorFn: (row) =>
              signStatusLabel.find(
                (status) => status.value === row.yousignStatus,
              )?.label,
            header: ({ column }) => (
              <Table.HeaderFilter
                label={t('Documents-directory.signature')}
                column={column as Column}
                options={signStatusLabel}
              >
                <p className="flex items-center gap-1">
                  <SignatureIcon className="size-5" />
                  {t('Documents-directory.signature')}
                </p>
              </Table.HeaderFilter>
            ),
            cell: ({ row }) => {
              const yousignStatus =
                signStatusLabel.find(
                  (status) => status.value === row.original.yousignStatus,
                ) || null

              return (
                <Link
                  href={`${pathname}/details/${row.original.id}`}
                  className="flex w-full items-center justify-center"
                >
                  {yousignStatus ? (
                    <Badge
                      variant={yousignStatus?.style}
                      className="font-medium"
                    >
                      <DynamicIcon
                        name={yousignStatus?.icon}
                        className="mr-1"
                      />
                      <p className="line-clamp-1">{yousignStatus?.label}</p>
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="font-medium">
                      {t('Documents-directory.signature-not-performed')}
                    </Badge>
                  )}
                </Link>
              )
            },
          },
        ]
      : []),
    {
      accessorKey: 'document',
      meta: {
        label: t('Navigation.orders-directory-documents'),
        csvLabel: t('Navigation.orders-directory-documents'),
      },
      id: 'document',
      header: () => (
        <p className="text-center">
          {t('Navigation.orders-directory-documents')}
        </p>
      ),
      cell: ({ row }) => {
        const order = row.original
        const isDownloading = downloadingDocuments.has(order.yousignID || '')
        const hasSignedDocument =
          order.yousignID && order.yousignStatus === 'COMPLETED'
        const hasInvoice =
          (order.status === 'DONE' && order.type === 'CART') ||
          order.type === 'FAVORITE'

        if (!hasInvoice && !hasSignedDocument)
          return <p className="text-center">-</p>

        return (
          <div className="flex items-center justify-center gap-1">
            {order.status === 'DONE' && order.type === 'CART' && (
              <Tooltip.Root>
                <PDFDownloader
                  filename="facture.pdf"
                  document={
                    <PDFInvoice
                      order={order as unknown as OrderModalValues}
                      enableTax={order.vat ? true : false}
                      teamLogo={order.shop.logo}
                    />
                  }
                >
                  <Tooltip.Trigger asChild>
                    <Button variant="outline" size="tiny">
                      <FileArrowDownIcon className="size-4" />
                    </Button>
                  </Tooltip.Trigger>
                </PDFDownloader>
                <Tooltip.Content className="my-0.5">
                  <p>{t('Order.generateInvoice')}</p>
                </Tooltip.Content>
              </Tooltip.Root>
            )}
            {hasSignedDocument && (
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <Button
                    variant="outline"
                    size="tiny"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleDownloadSignedDocument(order.yousignID)
                    }}
                    disabled={isDownloading}
                  >
                    <SignatureIcon
                      className={cx('size-4', {
                        'animate-pulse': isDownloading,
                      })}
                    />
                  </Button>
                </Tooltip.Trigger>
                <Tooltip.Content>
                  <p>
                    {isDownloading
                      ? t('Documents-directory.downloading')
                      : t('Documents-directory.download_signed_document')}
                  </p>
                </Tooltip.Content>
              </Tooltip.Root>
            )}
          </div>
        )
      },
    } as ColumnDef<Order>,
    {
      accessorKey: 'date',
      id: 'date',
      meta: {
        label: t('Property.DATE'),
        csvLabel: t('Property.DATE'),
      },
      header: ({ column }) => (
        <Table.DateFilter
          column={column as Column}
          label={t('Property.DATE')}
          isPeriodicCalendar={true}
          isNormalCalendar={true}
          language={(locale as SupportedLanguages) || 'fr'}
        />
      ),
      filterFn: Table.dateRangeFilterFn,
      cell: ({ row }) => <div>{row.original.date}</div>,
    },
    ...(view === 'sentquote' || view === 'seller'
      ? [
          {
            accessorKey: 'actions',
            meta: {
              label: t('Field.action'),
            },
            header: () => <div className="sr-only">Actions</div>,
            cell: ({ row }) => {
              const order = row.original
              return <OrderActionsCell order={order} view={view} />
            },
            maxSize: 50,
            fixed: true,
          } as ColumnDef<Order> & { fixed?: boolean },
        ]
      : []),
  ]

  const displayedOrders = useMemo(
    () => (hasReceivedFilteredRows ? filteredOrders : (orders ?? [])),
    [hasReceivedFilteredRows, filteredOrders, orders],
  )

  const totals = useMemo(() => {
    return (
      sumOrdersByCurrency(displayedOrders ?? [])
        .map(({ currency, value }) => formatPrice({ amount: value, currency }))
        .join(' + ') ||
      (displayedOrders.length > 0
        ? '-'
        : formatPrice({ amount: 0, currency: 'EUR' }))
    )
  }, [displayedOrders])

  const orderTitle = () => {
    switch (view) {
      case 'sentquote':
        return 'devis créés'
      case 'seller':
        return `ventes`
      case 'request':
        return 'demandes'
      case 'sent':
        return 'demandes envoyées'
      case 'buyer':
        return 'achats'
      default:
        return 'commandes'
    }
  }

  const handleCreateQuote = () => {
    setOrderType('QUOTE')
    setSuccessConfirmationModal(true)
    setPage('orders')
    router.push('/orders/quote')
  }

  return (
    <DataTable
      data={orders || []}
      variant="light"
      columns={ordersColumns}
      isLoaded={isFetched}
      pageSize={10}
      search={['name']}
      searchMessage={t('Toolbar.search')}
      enableRowSelection
      emptyMessage={
        isFetched && orders?.length
          ? t('Search.no_results_description')
          : t('Search.no_results')
      }
      pagination
      clearFiltersLabel={t('Toolbar.clear_filters')}
      onFilteredRowsChange={handleFilteredRowsChange}
      actionBtn={
        <div className="flex items-center gap-2">
          {view === 'sentquote' && (
            <Button onClick={handleCreateQuote}>
              {t('Navigation.create-quote')}
            </Button>
          )}
          <div className="flex flex-col items-start">
            <span className="text-sm text-slate-300">
              {displayedOrders?.length || 0} {orderTitle()}
              {displayedOrders?.length !== orders?.length && (
                <span className="text-slate-400"> / {orders?.length || 0}</span>
              )}
            </span>
            {(view === 'seller' || view === 'sentquote') && (
              <span className="text-sm text-slate-300">
                {t('Order.ca')}: {totals}
              </span>
            )}
          </div>
        </div>
      }
    />
  )
}
